import { QuestionCircleOutlined } from '@ant-design/icons';
import { Modal } from 'antd';
import ModuleCard from 'components/cards/ModuleCard';
import AppColors from 'config/AppColors';
import useSymptomTrackingData from 'hooks/useSymptomTrackingData';
import { locale } from 'locales/i18nManager';
import posthog from 'posthog-js';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

const useStyles = createUseStyles({
  nextPeriodPredictionContainer: {
    width: 'fit-content',
    textAlign: 'center',
    '& .ant-card-body': {
      padding: '24px 24px 20px',
    },
    position: 'relative',
  },
  nextPeriodDatesPrediction: {
    fontSize: 13,
    fontWeight: 500,
    color: AppColors.text.veryLightGray,
    margin: 0,
  },
  nextPeriodDescription: {
    fontSize: 13,
    fontWeight: 500,
    color: AppColors.text.lightGray,
    margin: 0,
  },
  nextPeriodPrediction: {
    fontSize: 28,
    fontWeight: 700,
    margin: 0,
  },
  periodPredictionHelpContainer: {
    position: 'absolute',
    top: 0,
    right: -24,
    '& svg': {
      color: AppColors.text.veryLightGray,
    },
  },
});

const NextPeriodPrediction: React.FC = () => {
  const styles = useStyles();
  const { t } = useTranslation();
  const [showPeriodPredictionHelp, setShowPeriodPredictionHelp] = useState(false);
  const { daysUntilNextPeriod, probableNextPeriodDates, isWithinPeriod, probableDaysUntilEndOfPeriod } =
    useSymptomTrackingData();

  const renderWhenNotInPeriod = () => (
    <>
      <h3 className={styles.nextPeriodDescription}>{t('symptomTracking.nextPeriod.nextPeriodIn')}</h3>
      <p className={styles.nextPeriodPrediction}>
        {/* eslint-disable-next-line no-nested-ternary */}
        {daysUntilNextPeriod ? (
          daysUntilNextPeriod.min === daysUntilNextPeriod.max ? (
            `${daysUntilNextPeriod.min} ${t(daysUntilNextPeriod.min === 1 ? 'symptomTracking.nextPeriod.day' : 'symptomTracking.nextPeriod.days')}`
          ) : (
            `${daysUntilNextPeriod.min} ${t('symptomTracking.nextPeriod.to')} ${daysUntilNextPeriod.max} ${t('symptomTracking.nextPeriod.days')}`
          )
        ) : (
          <>
            <span style={{ color: AppColors.text.veryLightGray }}>-</span> {t('symptomTracking.nextPeriod.days')}
          </>
        )}
      </p>
      <p className={styles.nextPeriodDatesPrediction}>
        {/* eslint-disable-next-line no-nested-ternary */}
        {probableNextPeriodDates &&
          `${probableNextPeriodDates.start.toDate().toLocaleDateString(locale, { month: 'short', day: 'numeric' })} - ${probableNextPeriodDates.end.toDate().toLocaleDateString(locale, { month: 'short', day: 'numeric' })}`}
      </p>
    </>
  );

  const renderWhenInPeriod = () => (
    <>
      <h3 className={styles.nextPeriodDescription}>{t('symptomTracking.nextPeriod.endPeriodIn')}</h3>
      <p className={styles.nextPeriodPrediction}>
        {/* eslint-disable-next-line no-nested-ternary */}
        {probableDaysUntilEndOfPeriod ? (
          `${probableDaysUntilEndOfPeriod} ${t(probableDaysUntilEndOfPeriod === 1 ? 'symptomTracking.nextPeriod.day' : 'symptomTracking.nextPeriod.days')}`
        ) : (
          <>
            <span style={{ color: AppColors.text.veryLightGray }}>-</span> {t('symptomTracking.nextPeriod.days')}
          </>
        )}
      </p>
    </>
  );

  return (
    <div className={styles.nextPeriodPredictionContainer}>
      <ModuleCard block={false}>{isWithinPeriod ? renderWhenInPeriod() : renderWhenNotInPeriod()}</ModuleCard>
      <div className={styles.periodPredictionHelpContainer}>
        <QuestionCircleOutlined
          data-testid="tracking.next-period-prediction.help"
          onClick={() => {
            setShowPeriodPredictionHelp(true);
            posthog.capture('tracking.period_prediction_help_opened');
          }}
        />
        <Modal
          open={showPeriodPredictionHelp}
          onCancel={() => setShowPeriodPredictionHelp(false)}
          footer={<span />}
          title={t('symptomTracking.nextPeriod.helpTitle')}
        >
          <p>
            <Trans i18nKey="symptomTracking.nextPeriod.help" components={{ br: <br /> }} />
          </p>
        </Modal>
      </div>
    </div>
  );
};

export default NextPeriodPrediction;
